import Hls from 'hls.js';
import { HlsPlayerAdapter } from './hls-player.js';
import { NativeHlsPlayerAdapter } from './native-hls-player.js';
/**
 * 재생 어댑터 선택. **기능 검사 하나로 정한다 — UA 로 고르지 않는다.**
 *
 * `client-type.ts` 가 UA 픽스처 12종으로 값을 치른 자리이고(iPadOS 는 자기를 `Macintosh`
 * 라고 말한다), 여기서 UA 를 다시 읽으면 그 판정이 두 벌이 된다. 우리가 알고 싶은 것은
 * 「이 기기가 무엇인가」가 아니라 **「hls.js 가 여기서 도는가」** 하나다.
 *
 * `Hls.isSupported()` 는 MSE(`MediaSource` + `SourceBuffer`)의 유무를 본다.
 * - **참** — 데스크톱·Android. 지금까지의 경로 그대로다
 * - **거짓** — iPhone. MSE 가 없어 `new Hls()` 가 **던지지도 오류를 내지도 않고 조용히
 *   멈춘다** (`docs/30` §1). 그래서 네이티브 HLS 로 간다
 *
 * **iOS 17.1+ 의 `ManagedMediaSource` 가 참을 만들 수도 있다.** 그때는 hls.js 경로로
 * 가는데, 기종을 넓히면 iOS 에서 hls.js 를 쓸 이유가 없다는 것이 `docs/29` D6 의 판단이다 —
 * 필요해지면 이 함수 하나를 고친다. **고칠 자리가 하나인 것이 이 파일의 존재 이유다.**
 */
export function createPlayerAdapter(opts) {
    return resolvePlayerKind() === 'hls'
        ? new HlsPlayerAdapter(opts)
        : new NativeHlsPlayerAdapter(opts);
}
/**
 * 어느 경로로 갈 것인가. 화면이 이 값을 진단에 노출한다 — **재생이 안 될 때 가장 먼저
 * 알아야 하는 값**이고, iOS 에는 하네스가 붙지 않아(`docs/29` §10) Safari 웹 인스펙터로
 * 눈으로 읽는 것이 유일한 관찰 창이다.
 */
export function resolvePlayerKind() {
    return nativeHlsPreferred() || !Hls.isSupported() ? 'native' : 'hls';
}
/**
 * ★ **이 기기는 HLS 를 직접 트는가** (v19, 2026-08-20).
 *
 * ── 무엇이 바뀌었나 ──────────────────────────────────────────
 *
 * 위 주석이 예고한 「iOS 17.1+ 의 `ManagedMediaSource` 가 참을 만들 수도 있다」가
 * **실기에서 일어났다.** hls.js 1.6.16 의 `getMediaSource(preferManagedMediaSource = true)`
 * 는 **`ManagedMediaSource` 가 있으면 그것을 우선** 돌려주므로, iPhone 13 · iOS 26 에서
 * `Hls.isSupported()` 가 **참**이 되고 아이폰이 **이 저장소에서 처음으로 hls.js 경로**로
 * 갔다. 증상은 **첫 재생이 `PLAYBACK_FAILED` 이고 「다시 시도」를 누르면 된다**였다.
 *
 * ── 왜 판정을 뒤집는가 ───────────────────────────────────────
 *
 * **iOS 에서 hls.js 를 쓸 이유가 없다**는 것이 `docs/29` D6 의 판단이고, 네이티브 HLS 는
 * **실기로 증명된 경로**다(`docs/30`·`docs/31` — 더블 버퍼까지 성립한다). 그러니 「MSE 가
 * 있는가」가 아니라 **「이 기기가 HLS 를 직접 트는가」**를 먼저 묻는 것이 옳다.
 *
 * 그리고 이 값은 **재생 경로 하나만 정하지 않는다** — `ChannelGate` 의 `gateOnSwitch` 가
 * 같은 함수를 보고 **채널 전환 뒤 게이트를 세울지**를 정한다. `'hls'` 로 판정되면 그
 * 게이트가 꺼지는데, **WebKit 은 소리 있는 재생 허가를 엘리먼트 단위로 기억하므로**
 * 전환이 제스처 밖에서 시작돼 그대로 실패한다.
 *
 * ── UA 를 읽지 않는다 ────────────────────────────────────────
 *
 * 위 주석의 그 규칙 그대로다. 묻는 것은 **기능 둘**이고 —
 * `canPlayType('application/vnd.apple.mpegurl')`(네이티브 HLS)과 `ManagedMediaSource`
 * 의 유무 — 크롬·파이어폭스는 앞의 것이 거짓이라 지금까지의 경로 그대로다.
 *
 * **사파리 계열이 통째로 네이티브로 간다** — macOS 사파리도 포함이다. 그쪽에서 전환
 * 수치를 잰 적이 없어 **넓힌 것이 맞다**(하네스와 데모 노트북은 크롬이라 그 축은 그대로다).
 * 좁히려면 「`MediaSource` 가 없다」를 더해야 하는데, **아이폰이 그것을 노출하는지
 * 확인하지 못했다** — 확인되지 않은 가정 위에 조건을 세우지 않는다.
 */
function nativeHlsPreferred() {
    if (typeof document === 'undefined' || typeof self === 'undefined')
        return false;
    if (!('ManagedMediaSource' in self))
        return false;
    try {
        return document.createElement('video').canPlayType('application/vnd.apple.mpegurl') !== '';
    }
    catch {
        return false;
    }
}
//# sourceMappingURL=create-player.js.map